import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpload } from '../context/UploadContext';
import { Upload, CheckCircle, XCircle, Loader2, ChevronUp, ChevronDown, X, FileText } from 'lucide-react';

/**
 * Floating upload progress widget — always visible in the bottom-right corner
 * when there are active or recently completed uploads.
 */
export default function UploadProgressIndicator() {
  const {
    jobs,
    isUploading,
    hasNewResults,
    lastBatchResults,
    clearCompleted,
    clearAll,
    dismissResults,
    pendingCount,
    uploadingCount,
    doneCount,
    failedCount,
    totalCount,
    activeCount,
  } = useUpload();

  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);

  // Don't render if there's nothing to show
  if (totalCount === 0 && !hasNewResults) return null;

  const progressPct = totalCount > 0
    ? Math.round(((doneCount + failedCount) / totalCount) * 100)
    : 0;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 shadow-2xl rounded-lg overflow-hidden border border-gray-200 bg-white">
      {/* Header bar */}
      <div
        className={`flex items-center justify-between px-4 py-2.5 cursor-pointer select-none ${
          isUploading ? 'bg-blue-600 text-white' :
          failedCount > 0 ? 'bg-amber-500 text-white' :
          doneCount > 0 ? 'bg-green-600 text-white' :
          'bg-gray-700 text-white'
        }`}
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-2">
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : failedCount > 0 ? (
            <XCircle className="h-4 w-4" />
          ) : doneCount > 0 ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span className="text-sm font-medium">
            {isUploading
              ? `Uploading ${activeCount} file${activeCount !== 1 ? 's' : ''}...`
              : doneCount > 0 || failedCount > 0
                ? `Upload complete — ${doneCount} done${failedCount > 0 ? `, ${failedCount} failed` : ''}`
                : `${totalCount} file${totalCount !== 1 ? 's' : ''} queued`
            }
          </span>
        </div>
        <div className="flex items-center gap-1">
          {!isUploading && (
            <button
              onClick={(e) => { e.stopPropagation(); clearAll(); }}
              className="p-0.5 hover:bg-white/20 rounded"
              title="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronUp className="h-4 w-4" />
          )}
        </div>
      </div>

      {/* Progress bar */}
      {isUploading && (
        <div className="h-1 bg-blue-200">
          <div
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}

      {/* Expanded file list */}
      {expanded && (
        <div className="max-h-64 overflow-y-auto">
          {jobs.map(job => (
            <div
              key={job.id}
              className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 last:border-0"
            >
              <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-800 truncate">{job.name}</p>
                <p className="text-[10px] text-gray-500">
                  {(job.size / 1024 / 1024).toFixed(1)} MB
                </p>
              </div>
              <div className="flex-shrink-0">
                {job.status === 'pending' && (
                  <span className="text-[10px] text-gray-400 font-medium">Queued</span>
                )}
                {job.status === 'uploading' && (
                  <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                )}
                {job.status === 'done' && (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                )}
                {job.status === 'failed' && (
                  <span title={job.error}>
                    <XCircle className="h-4 w-4 text-red-500" />
                  </span>
                )}
              </div>
            </div>
          ))}

          {/* Actions footer */}
          {!isUploading && totalCount > 0 && (
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t">
              <button
                onClick={() => { navigate('/invoices'); clearCompleted(); }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                View Invoices
              </button>
              <button
                onClick={clearCompleted}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
